import React, { useRef, useMemo, useState, useEffect } from "react";
import {
  PAPER_SIZES,
  RichContentRenderer,
  setInitialExamData,
} from "../TepmpateEditor/utility.js";
import { getExaminationTemplateDetails } from "../../../services/Users-services.js";
import { useParams } from "react-router-dom";
/**
 * QuestionPaperViewer Component
 *
 * Renders a read-only view of the question paper from backend data.
 *
 * @param {Object} backendData - Raw backend response data (optional - if not provided, will fetch using examId)
 * @param {String} examId - Exam ID to fetch data for (required if backendData not provided)
 * @param {String} paperSize - Paper size for rendering (A4, A3, Letter, etc.) - defaults to A4
 * @param {Function} fetchDetails - Custom fetch function (optional - defaults to getExaminationTemplateDetails)
 *
 * Usage:
 * // With pre-loaded data
 * <QuestionPaperViewer backendData={apiResponse.response} />
 *
 * // Fetch data automatically
 * <QuestionPaperViewer examId="123456" />
 *
 * // With custom paper size
 * <QuestionPaperViewer examId="123456" paperSize="A4" />
 */
const QuestionPaperViewer = ({
  backendData,
  examId,
  paperSize = "A4",
  fetchDetails = getExaminationTemplateDetails,
}) => {
  const canvasRef = useRef(null);
 const {id} = useParams();
 examId = examId || id; // Use examId from props or URL params
  // Validate props
  if (!backendData && !examId) {
    console.warn(
      "QuestionPaperViewer: Either 'backendData' or 'examId' prop must be provided"
    );
  }

  // State for data fetching
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchedData, setFetchedData] = useState(null);

  // Fetch exam data if not provided and examId is available
  useEffect(() => {
    const fetchExamData = async () => {
      if (backendData || !examId) {
        return; // Don't fetch if we already have data or no examId provided
      }

      setLoading(true);
      setError(null);

      try {
        console.log("QuestionPaperViewer: Fetching exam data for ID:", examId);
        const response = await fetchDetails(examId);

        // Handle both "success" and "200" status responses
        if (response.status === "success" || response.status === "200") {
          console.log(
            "QuestionPaperViewer: Successfully fetched exam data:",
            response
          );
          setFetchedData(response);
        } else {
          throw new Error(response.message || "Failed to fetch exam data");
        }
      } catch (err) {
        console.error("QuestionPaperViewer: Error fetching exam data:", err);
        setError(err.message || "Failed to load exam data");
      } finally {
        setLoading(false);
      }
    };

    fetchExamData();
  }, [examId, backendData, fetchDetails]);

  // Use either provided data or fetched data
  const rawData = backendData || fetchedData;

  // Transform backend data to frontend exam structure
  const examData = useMemo(() => {
    if (!rawData) {
      return null;
    }

    // Extract the actual exam data - handle multiple API response formats
    let apiData = null;

    // Case 1: Data is in rawData.data (like QuestionPaperBuilder expects)
    if (rawData.data) {
      apiData = rawData.data;
      console.log("QuestionPaperViewer: Using rawData.data");
    }
    // Case 2: Data is in rawData.response (like the test data structure)
    else if (rawData.response) {
      apiData = rawData.response;
      console.log("QuestionPaperViewer: Using rawData.response");
    }
    // Case 3: rawData is the direct API data (already unwrapped)
    else if (rawData.NAME || rawData["_TEMPLATE SECTIONS_"] || rawData._ID) {
      apiData = rawData;
      console.log("QuestionPaperViewer: Using rawData directly");
    }

    if (!apiData) {
      console.warn(
        "QuestionPaperViewer: No valid API data found in response structure:",
        rawData
      );
      return null;
    }

    try {
      console.log(
        "QuestionPaperViewer: Transforming data to exam structure with apiData:",
        apiData
      );
      const transformedData = setInitialExamData(apiData);
      console.log(
        "QuestionPaperViewer: Successfully transformed exam data:",
        transformedData
      );
      return transformedData;
    } catch (error) {
      console.error("QuestionPaperViewer: Error transforming data:", error);
      console.error(
        "QuestionPaperViewer: Raw API data that failed to transform:",
        apiData
      );
      return null;
    }
  }, [rawData]);

  // Loading state
  if (loading) {
    return (
      <div className="question-paper-viewer">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading exam data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="question-paper-viewer">
        <div className="error-container">
          <p className="error-message">Error: {error}</p>
          <p className="error-details">
            Unable to load exam data. Please check your connection and try
            again.
          </p>
        </div>
      </div>
    );
  }

  // No data state
  if (!examData) {
    return (
      <div className="question-paper-viewer">
        <div className="no-data">
          <p>No exam data available</p>
          {!backendData && !examId && (
            <p className="error-details">
              Please provide either 'backendData' or 'examId' prop.
            </p>
          )}
          {rawData && (
            <p className="error-details">
              Unable to process the provided exam data. Please check the console
              for details.
            </p>
          )}
        </div>
      </div>
    );
  }

  const currentPageSize = PAPER_SIZES[paperSize];

  // Calculate total marks
  const calculateTotalMarks = () => {
    return examData.pages.reduce(
      (total, page) =>
        total +
        page.sections.reduce(
          (sectionTotal, section) =>
            sectionTotal +
            section.questions.reduce(
              (questionTotal, question) =>
                questionTotal + (question.marks || 1),
              0
            ),
          0
        ),
      0
    );
  };

  // Use the same pagination logic as Canvas component
  const organizeContentIntoPages = () => {
    const pageHeight = currentPageSize.height - 160;
    const questionBaseHeight = 75;
    const optionHeight = 25;
    const sectionHeaderHeight = 50;
    const examHeaderHeight = 200;
    const pageHeaderHeight = 20;
    const pagePadding = 30;
    const sectionInstructionsHeight = 35;
    const sectionMargin = 15;

    let organizedPages = [];
    let currentPageHeight = 0;
    let currentPage = null;
    let pageIndex = 0;

    const createNewPage = () => {
      const newPage = {
        id: `page-${Date.now()}-${pageIndex}`,
        padding: 20,
        borderStyle: "solid",
        pageBreakBefore: pageIndex > 0,
        sections: [],
      };

      if (pageIndex === 0) {
        currentPageHeight = examHeaderHeight + pagePadding;
      } else {
        currentPageHeight = pageHeaderHeight + pagePadding;
      }

      pageIndex++;
      return newPage;
    };

    const calculateQuestionHeight = (question) => {
      let height = questionBaseHeight;

      if (
        question.type === "with-options" &&
        question.options &&
        question.options.length > 0
      ) {
        height += question.options.length * optionHeight;
      }

      if (question.richContent && question.richContent.html) {
        const htmlLength = question.richContent.html.length;
        if (htmlLength > 200) {
          height += Math.floor(htmlLength / 100) * 15;
        }
      }

      return height;
    };

    const canFitOnCurrentPage = (additionalHeight) => {
      return currentPageHeight + additionalHeight <= pageHeight;
    };

    const allSections = examData.pages.flatMap((page) => page.sections);

    if (allSections.length === 0) {
      const emptyPage = createNewPage();
      organizedPages.push(emptyPage);
      return organizedPages;
    }

    currentPage = createNewPage();

    allSections.forEach((section) => {
      if (!section.questions || section.questions.length === 0) {
        return;
      }

      let sectionHeight = sectionHeaderHeight;
      let sectionQuestionsForCurrentPage = [];
      let remainingQuestions = [...section.questions];

      if (
        section.hasInstructions &&
        section.instructions &&
        section.instructions.plainText &&
        section.instructions.plainText.trim() !== "" &&
        section.instructions.plainText !== "Enter section instructions here..."
      ) {
        sectionHeight += sectionInstructionsHeight;
      }

      while (remainingQuestions.length > 0) {
        const question = remainingQuestions[0];
        const questionHeight = calculateQuestionHeight(question);

        let totalHeightNeeded = questionHeight;

        if (sectionQuestionsForCurrentPage.length === 0) {
          totalHeightNeeded += sectionHeight + sectionMargin;
        }

        if (canFitOnCurrentPage(totalHeightNeeded)) {
          sectionQuestionsForCurrentPage.push(remainingQuestions.shift());
          currentPageHeight +=
            sectionQuestionsForCurrentPage.length === 1
              ? totalHeightNeeded
              : questionHeight;
        } else {
          if (sectionQuestionsForCurrentPage.length > 0) {
            currentPage.sections.push({
              ...section,
              questions: sectionQuestionsForCurrentPage,
              continuesOnNextPage: remainingQuestions.length > 0,
            });
            sectionQuestionsForCurrentPage = [];
          }

          organizedPages.push(currentPage);
          currentPage = createNewPage();

          totalHeightNeeded = questionHeight + sectionHeight + sectionMargin;

          if (canFitOnCurrentPage(totalHeightNeeded)) {
            sectionQuestionsForCurrentPage.push(remainingQuestions.shift());
            currentPageHeight += totalHeightNeeded;
          } else {
            console.warn(`Question is too large for page: ${question.id}`);
            sectionQuestionsForCurrentPage.push(remainingQuestions.shift());
            currentPageHeight += totalHeightNeeded;
          }
        }
      }

      if (sectionQuestionsForCurrentPage.length > 0) {
        currentPage.sections.push({
          ...section,
          questions: sectionQuestionsForCurrentPage,
        });
      }

      currentPageHeight += sectionMargin / 2;
    });

    if (
      currentPage &&
      (currentPage.sections.length > 0 || organizedPages.length === 0)
    ) {
      organizedPages.push(currentPage);
    }

    if (organizedPages.length === 0) {
      const emptyPage = createNewPage();
      organizedPages.push(emptyPage);
    }

    return organizedPages;
  };

  const organizedPages = organizeContentIntoPages();
  const totalMarks = calculateTotalMarks();

  // Format time for display
  const formatTimeForDisplay = (duration) => {
    if (!duration || duration === "00:00:00") {
      return "Not specified";
    }
    const [hours, minutes] = duration.split(":");
    if (hours === "00" && minutes === "00") {
      return "Not specified";
    }
    return `${parseInt(hours)} Hours ${parseInt(minutes)} Minutes`;
  };

  // Create exam header component
  const renderExamHeader = () => (
    <div className="exam-header">
      <div className="exam-heading">
        {examData.metadata?.heading || examData.name || "EXAMINATION"}
      </div>
      <div className="exam-subheading">
        {examData.metadata?.subheading || "Standard - Section"}
      </div>
      <div className="exam-subject">
        {examData.metadata?.subjectName || "Subject"}
      </div>
      <div className="exam-details">
        <div className="exam-time">
          Time Allowed:{" "}
          <span className="time-value">
            {examData.metadata?.timeAllowed ||
              formatTimeForDisplay(examData.duration)}
          </span>
        </div>
        <div className="exam-marks">
          Maximum Marks:{" "}
          <span className="marks-value">
            {examData.metadata?.totalMarks || examData.score || totalMarks}
          </span>
        </div>
      </div>
      <div className="exam-instructions">
        <strong>Instructions:</strong>{" "}
        <span className="instructions-text">
          <RichContentRenderer
            content={
              examData.metadata?.instructionsRichContent || {
                type: "rich-text",
                html: `<span>${
                  examData.metadata?.instructions ||
                  examData.instructions ||
                  "Follow all instructions carefully."
                }</span>`,
                plainText:
                  examData.metadata?.instructions ||
                  examData.instructions ||
                  "Follow all instructions carefully.",
              }
            }
          />
        </span>
      </div>
    </div>
  );

  const renderQuestion = (question, questionIndex) => {
    return (
      <div key={question.id} className="question-item">
        <div className="question-number">{questionIndex + 1}.</div>
        <div className="question-content">
          <div className="question-text">
            <RichContentRenderer
              content={
                question.richContent || {
                  type: "text",
                  content: question.content,
                }
              }
            />
          </div>

          {question.type === "with-options" &&
            question.options &&
            question.options.length > 0 && (
              <div
                className={`question-options ${
                  question.optionsLayout || "vertical"
                }`}
              >
                {question.options.map((option, optionIndex) => (
                  <div key={option.id || optionIndex} className="option-item">
                    <span className="option-label">
                      ({String.fromCharCode(97 + optionIndex)})
                    </span>
                    <span className="option-text">
                      <RichContentRenderer
                        content={
                          option.richContent || {
                            type: "text",
                            content: option.content || option,
                          }
                        }
                      />
                    </span>
                  </div>
                ))}
              </div>
            )}

          <div className="question-marks">
            [{question.marks || 1} mark{(question.marks || 1) !== 1 ? "s" : ""}]
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (section, sectionIndex, pageIndex) => {
    const hasQuestions = section.questions && section.questions.length > 0;
    const hasInstructions =
      section.hasInstructions &&
      section.instructions &&
      section.instructions.plainText &&
      section.instructions.plainText.trim() !== "" &&
      section.instructions.plainText !== "Enter section instructions here...";

    if (!hasQuestions) {
      return null;
    }

    return (
      <div key={section.id} className="section">
          <div className="section-title-content">
                {section.titleRichContent ? (
                  <RichContentRenderer
                    content={section.titleRichContent}
                    isEditMode={false}
                  />
                ) : (
                  <span className="section-title-plain">{section.title}</span>
                )}
              </div>

        {hasInstructions && (
          <div className="section-instructions">
            <div className="instructions-label">Instructions:</div>
            <div className="instructions-text">
              <RichContentRenderer content={section.instructions} />
            </div>
          </div>
        )}

        <div className="section-content">
          {section.questions.map((question, questionIndex) => {
            // Calculate global question offset
            let questionOffset = 0;
            for (let i = 0; i < pageIndex; i++) {
              for (let j = 0; j < organizedPages[i].sections.length; j++) {
                questionOffset +=
                  organizedPages[i].sections[j].questions.length;
              }
            }
            for (let k = 0; k < sectionIndex; k++) {
              questionOffset +=
                organizedPages[pageIndex].sections[k].questions.length;
            }

            return renderQuestion(question, questionOffset + questionIndex);
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="question-paper-viewer">
      <div
        ref={canvasRef}
        className="paper-canvas"
        style={{
          width: "100%",
          maxWidth: "none",
        }}
      >
        {organizedPages.map((page, pageIndex) => (
          <div
            key={page.id}
            className={`page ${
              pageIndex < organizedPages.length - 1
                ? "page-with-bottom-margin"
                : ""
            }`}
            style={{
              padding: `${page.padding}px`,
              border:
                page.borderStyle !== "none"
                  ? `1px ${page.borderStyle} #e1e1e1`
                  : "none",
              width: "100%",
              minHeight: currentPageSize.height - 80,
              boxSizing: "border-box",
            }}
          >
            {/* Add exam header only to first page */}
            {pageIndex === 0 && renderExamHeader()}

            <div className="page-header">
              <span className="page-number">
                Page {pageIndex + 1} of {organizedPages.length}
              </span>
            </div>

            {page.sections.map((section, sectionIndex) => {
              return (
                <React.Fragment key={section.id}>
                  {renderSection(section, sectionIndex, pageIndex)}
                  {/* Section divider */}
                  {sectionIndex < page.sections.length - 1 && (
                    <div className="section-divider"></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ))}

        <div className="page-info-footer">
          <div className="total-pages-info">
            Total Pages: {organizedPages.length} | Paper Size:{" "}
            {currentPageSize.name} | Total Marks: {totalMarks}
          </div>
        </div>
      </div>

      <style jsx>{`
        .question-paper-viewer {
          padding: 1rem;
          overflow-x: auto;
          overflow-y: auto;
          background: #f8fafc;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          min-height: 100vh;
        }

        .no-data,
        .loading-container,
        .error-container {
          text-align: center;
          color: #64748b;
          font-size: 1.1rem;
          margin-top: 2rem;
          padding: 2rem;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #e2e8f0;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .loading-text {
          margin: 0;
          font-size: 1rem;
          color: #374151;
        }

        .error-container {
          color: #ef4444;
        }

        .error-message {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .error-details {
          font-size: 0.9rem;
          color: #64748b;
          margin-top: 0.5rem;
          font-style: italic;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .paper-canvas {
          background: #ffffff00;
          border-radius: 4px;
          margin: 10px;
          margin-bottom: 20px;
          width: 100%;
          min-width: 900px;
          font-family: "Times New Roman", Georgia, serif;
        }

        .exam-header {
          text-align: center;
          padding: 0.75rem 1rem 0.5rem;
          border-bottom: 2px solid #1e293b;
          margin-bottom: 0.75rem;
          background: #ffffff;
        }

        .exam-heading {
          font-size: 1.2rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid #64748b;
          padding-bottom: 0.125rem;
          margin-bottom: 0.375rem;
          color: #1e293b;
        }

        .exam-subheading {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
          color: #1e293b;
        }

        .exam-subject {
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #1e293b;
        }

        .exam-details {
          display: flex;
          justify-content: space-between;
          margin: 0.5rem 0;
          font-weight: 600;
          color: #1e293b;
          border-top: 1px solid #64748b;
          border-bottom: 1px solid #64748b;
          padding: 0.375rem 0;
          font-size: 0.9rem;
        }

        .time-value,
        .marks-value {
          font-weight: 700;
        }

        .exam-instructions {
          text-align: left;
          margin: 0.5rem 0 0;
          padding: 0.375rem 0;
          line-height: 1.3;
          color: #1e293b;
          font-size: 0.85rem;
        }

        .instructions-text {
          display: inline;
        }

        .page {
          background: #ffffff;
          position: relative;
          overflow: hidden;
          width: 100%;
          border: none;
          padding: 0.75rem 1rem;
          margin-bottom: 2rem;
        }

        .page-with-bottom-margin {
          margin-bottom: 2rem;
          border-bottom: 2px dashed #94a3b8;
          padding-bottom: 1.5rem;
        }

        .page-header {
          display: flex;
          justify-content: flex-end;
          padding: 0;
          margin-bottom: 0.5rem;
          border: none;
          background: transparent;
        }

        .page-number {
          font-size: 0.65rem;
          color: #64748b;
          font-weight: 500;
          font-family: "Times New Roman", serif;
        }

        .section {
          margin: 0.5rem 0;
          padding: 0.5rem;
          background: #ffffff;
          width: 100%;
          box-sizing: border-box;
        }

        .section-header {
          margin-bottom: 0.375rem;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 0.125rem;
        }

        .section-title {
          font-size: 0.95rem;
          font-weight: 700;
          margin: 0;
          color: #1e293b;
          font-family: "Times New Roman", serif;
        }

        .section-instructions {
          margin-bottom: 1rem;
          padding: 0.75rem;
          background: #f0fdf4;
          border: 1px solid #dcfce7;
          border-radius: 6px;
        }

        .instructions-label {
          font-weight: 600;
          color: #166534;
          font-size: 0.85rem;
          margin-bottom: 0.5rem;
        }

        .section-instructions .instructions-text {
          color: #166534;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .section-content {
          padding-left: 0.25rem;
          flex: 1;
          min-height: 40px;
        }

        .section-divider {
          height: 1px;
          background: #d1d5db;
          margin: 0.5rem 0;
        }

        .question-item {
          display: flex;
          margin: 0.375rem 0;
          padding: 0.375rem;
          line-height: 1.4;
          width: 100%;
          box-sizing: border-box;
          font-family: "Times New Roman", serif;
        }

        .question-number {
          font-weight: 700;
          margin-right: 0.5rem;
          min-width: 1.5rem;
          color: #1e293b;
          font-size: 0.95rem;
          font-family: "Times New Roman", serif;
        }

        .question-content {
          flex: 1;
          width: 100%;
          min-width: 0;
        }

        .question-text {
          margin-bottom: 0.375rem;
          line-height: 1.4;
          color: #1e293b;
          width: 100%;
          word-wrap: break-word;
          font-family: "Times New Roman", serif;
        }

        .question-options {
          margin: 0.375rem 0;
          padding-left: 0.5rem;
        }

        .question-options.horizontal {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .question-options.grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.5rem;
        }

        .option-item {
          display: flex;
          margin: 0.125rem 0;
          align-items: flex-start;
        }

        .option-label {
          font-weight: 600;
          margin-right: 0.375rem;
          min-width: 1.25rem;
          color: #1e293b;
          font-family: "Times New Roman", serif;
        }

        .option-text {
          flex: 1;
          line-height: 1.4;
          color: #1e293b;
          font-family: "Times New Roman", serif;
        }

        .question-marks {
          font-size: 0.8rem;
          color: #64748b;
          margin-top: 0.375rem;
          text-align: right;
          font-style: italic;
          font-family: "Times New Roman", serif;
        }

        .page-info-footer {
          padding: 0.5rem;
          text-align: center;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          color: #64748b;
          font-size: 0.7rem;
          border-radius: 0 0 4px 4px;
          font-family: "Times New Roman", serif;
        }

        .total-pages-info {
          font-weight: 500;
        }

        /* Print Styles */
        @media print {
          .question-paper-viewer {
            padding: 0;
            background: white;
          }

          .page {
            page-break-after: always;
            padding: 8mm 5mm;
            margin: 0;
            border: none;
            box-shadow: none;
          }

          .page:last-child {
            page-break-after: avoid;
          }

          .page-info-footer {
            display: none;
          }
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .paper-canvas {
            min-width: 700px;
          }

          .exam-details {
            flex-direction: column;
            gap: 0.25rem;
          }
        }

        @media (max-width: 768px) {
          .question-paper-viewer {
            padding: 0.5rem;
          }

          .paper-canvas {
            margin: 0;
            min-width: 500px;
          }

          .page {
            padding: 0.75rem 1rem;
          }

          .question-options.horizontal,
          .question-options.grid {
            display: block;
          }
        }
      `}</style>
    </div>
  );
};

// Example usage:
/*
// Method 1: With pre-loaded API response (full response object)
const apiResponse = {
  "status": "200",
  "message": "Success", 
  "response": { "NAME": "Test Exam", "_TEMPLATE SECTIONS_": [...], ... }
};
<QuestionPaperViewer backendData={apiResponse} />

// Method 2: With just the exam data (unwrapped)
const examData = { "NAME": "Test Exam", "_TEMPLATE SECTIONS_": [...], ... };
<QuestionPaperViewer backendData={examData} />

// Method 3: Fetch data automatically by exam ID (recommended)
<QuestionPaperViewer examId="1391080953211056128" />

// Method 4: With custom paper size
<QuestionPaperViewer examId="1391080953211056128" paperSize="A3" />

// Method 5: With custom fetch function
<QuestionPaperViewer 
  examId="1391080953211056128" 
  fetchDetails={customFetchFunction}
/>

// Method 6: Following QuestionPaperBuilder pattern
const response = await getExaminationTemplateDetails(examId);
if (response.status === "success") {
  <QuestionPaperViewer backendData={{ data: response.data }} />
}
*/

export default QuestionPaperViewer;
